/**
 * The session card's second read, and the projection it feeds.
 *
 * The queue payload carries the fraction, the kind and the duration, so the **ring and the meta line
 * are drawn on the first paint** — attention-queue R11 honoured for everything that decides how the
 * card is SHAPED. `GET /workout-execution/{id}/display` adds the chip row, which the queue could not
 * carry without loading every session's children for every trainer.
 *
 * That split is also what makes the card load without moving: the chip row is one line high, reserved
 * whether or not this read has landed, and nothing else on the row waits for it.
 *
 * ── It runs for a row the trainer can SEE, not for every row drawn ───────────────────────────
 * The card draws its whole half of the queue, so the caller passes `onScreen` and this read waits
 * for it: a pulpit with eight sessions waiting costs the two reads its visible rows need, and the
 * third row's read starts when the third row is scrolled to. `shared/lib/useOnScreen` is the gate and says
 * why it is geometry rather than the row's index.
 */

import { useTranslation } from 'react-i18next';

import { useGetWorkoutExecutionIdDisplay } from '~/shared/api/generated/endpoints/workout-execution/workout-execution';

import type { SessionFeedback } from '../model/feedbackShape';
import { sessionDetail, type SessionDetail } from '../model/sessionDetail';
import { sessionReadout, type SessionReadout } from '../model/sessionVerdict';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import { useHomeFormats } from './useHomeFormats';

export interface SessionReadoutState {
  readonly readout: SessionReadout;
  /**
   * The chips are still on their way.
   *
   * `false` for a session with no execution id, where there is nothing to wait for and no chips are
   * ever coming — the card must not reserve a row it will never fill. Also `false` before the row
   * has been on screen, where the read has not started; the chip row reserves its height in CSS
   * regardless, so an off-screen row scrolls into view already the right size.
   */
  readonly chipsPending: boolean;
  /**
   * What the task card draws under the ring — the client's words and the exercises that deviated.
   * `null` until the same read lands, and after one that failed.
   */
  readonly detail: SessionDetail | null;
  /** The read behind `detail` failed — the detail is missing, not empty. */
  readonly detailFailed: boolean;
}

/**
 * @param onScreen Whether the row has been on screen. `false` holds the read; see the header.
 */
export function useSessionReadout(
  session: SessionFeedback,
  onScreen: boolean,
): SessionReadoutState {
  const { t } = useTranslation();
  const { decimal } = useHomeFormats();
  const contentLang = useContentLanguage();
  const hasExecution = session.workoutExecutionId !== null;
  const wanted = hasExecution && onScreen;

  const display = useGetWorkoutExecutionIdDisplay(
    // Guarded by `enabled` below; the generated hook types the id as a number, and a session with no
    // execution never reaches the network.
    session.workoutExecutionId ?? 0,
    // The exercise content is localised server-side, so the read carries the reader's language —
    // the same key the note composer invalidates by prefix.
    { lang: contentLang },
    { query: { enabled: wanted } },
  );

  return {
    readout: sessionReadout(session, display.data, t),
    // A FAILED read is not pending: the card keeps its ring, its meta line and its controls, and
    // simply has no chips. Losing five chips is not worth an error state on a card whose whole job
    // is the note the trainer is about to write.
    chipsPending: wanted && display.isPending,
    detail: display.data === undefined ? null : sessionDetail(display.data, decimal),
    detailFailed: wanted && display.isError,
  };
}
